/* eslint-disable react-hooks/exhaustive-deps */
import './styles.scss';
import 'react-phone-input-2/lib/style.css';

import 'yup-phone-lite';
import * as yup from 'yup';
import { AppContext } from '../../context';
import { Button } from '@goodtimeio/rain';
import { ERROR } from '../../types/errors';
import { ValidationError, markAsError } from '../../components/ValidationError';
import { useFormik } from 'formik';
import { useHistory, useParams } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import React, { useContext, useEffect } from 'react';
import Wrapper from '../../components/Wrapper';

interface SchedulingLinkDataForm {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    linkedInUrl?: string | null;
}

const SchedulingLinkData: React.FC = () => {
    const { schedulingLinkIntro, schedulingLinkDataSent, getSchedulingLinkIntro, sendSchedulingLinkData, translate } =
        useContext(AppContext);
    const history = useHistory();
    const { orgName: linkOrgName, customName: linkCustomName } = useParams<{ orgName: string; customName: string }>();
    const { data: intro, isLoading: isLoadingIntro = false, error: introError } = schedulingLinkIntro || {};
    const { data: dataSentResponse, isLoading: isSending = false, error: dataSentError } = schedulingLinkDataSent || {};
    const { schedulingLink, etchBrandTheme } = intro || {};
    const link = `${linkOrgName}/${linkCustomName}`;

    if (dataSentResponse?.interview?.hash) {
        history.push(`/intro/${dataSentResponse.interview.hash}`);
    }
    if (introError === String(ERROR.SCHEDULING_LINK_NOT_FOUND)) {
        history.push('/invalid-link');
    }

    const primaryColor = etchBrandTheme?.meetCompanyColor || '#6400E0';
    const backgroundImage = etchBrandTheme?.meetBackgroundUrlList[0];
    const logo = etchBrandTheme?.logoUrl;
    const textColor = etchBrandTheme?.meetFontColor || '#FFFFFF';

    const { values, errors, touched, submitForm, handleChange, handleBlur, setFieldValue } =
        useFormik<SchedulingLinkDataForm>({
            initialValues: {
                firstName: '',
                lastName: '',
                phone: '',
                email: '',
                linkedInUrl: '',
            },
            validationSchema: () =>
                yup.object().shape({
                    firstName: yup.string().required('Required').trim(),
                    lastName: yup.string().required('Required').trim(),
                    phone: yup.string().phone().required().trim(),
                    email: yup.string().email('Invalid email').required('Required'),
                    linkedInUrl: yup.string().test(
                        'invalidLinkedInURL',
                        'Invalid LinkedIn URL',
                        (value) =>
                            value
                                ? /^(https?:\/\/)?(www\.)?linkedin\.com\/in\/[a-zA-Z0-9_-]+\/?$/.test(value) // Regex for validating linkedIn
                                : true, // Return true as a valid value (linkedIn variable is not required)
                    ),
                }),
            onSubmit: (schedulingLinkData) =>
                sendSchedulingLinkData({
                    ...schedulingLinkData,
                    linkedInUrl: schedulingLinkData.linkedInUrl || undefined,
                    staticLink: link,
                }),
        });

    useEffect(() => {
        if (primaryColor) {
            document.documentElement.style.setProperty('--primary-date-picker-color', primaryColor);
        }
        if (textColor) {
            document.documentElement.style.setProperty('--primary-text-color', textColor);
        }
    }, [primaryColor, textColor]);

    useEffect(() => {
        getSchedulingLinkIntro(link);
    }, []);

    return (
        <Wrapper
            loading={isLoadingIntro || isSending}
            backgroundImage={backgroundImage}
            logo={logo}
            message={
                isLoadingIntro
                    ? 'Building the form for the interview'
                    : "Saving the information. It may take some minutes. Don't refresh the page"
            }
        >
            <div className="cardContainer">
                <div className="cardContent">
                    {(dataSentError || dataSentResponse?.interview === null) && (
                        <div className="error">
                            <span>{translate('link:generalErrorMessage')}</span>
                        </div>
                    )}
                    <div className="field">
                        <span className="required">{translate('link:firstName')}</span>
                        <input
                            name="firstName"
                            className={`input ${markAsError({ errors, touched, field: 'firstName' })}`}
                            type="text"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.firstName}
                        />
                        <ValidationError errors={errors} touched={touched} field="firstName" />
                    </div>
                    <div className="field">
                        <span className="required">{translate('link:lastName')}</span>
                        <input
                            name="lastName"
                            className={`input ${markAsError({ errors, touched, field: 'lastName' })}`}
                            type="text"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.lastName}
                        />
                        <ValidationError errors={errors} touched={touched} field="lastName" />
                    </div>
                    <div className="field">
                        <span className="required">{translate('link:phone')}</span>
                        <div
                            style={{ borderRadius: '8px' }}
                            className={markAsError({ errors, touched, field: 'phone' })}
                        >
                            <PhoneInput
                                country="us"
                                enableSearch
                                disableSearchIcon
                                inputProps={{ name: 'phone' }}
                                inputClass="phone inputControl"
                                buttonClass="phone flags"
                                searchStyle={{ width: '268px', borderRadius: '8px' }}
                                onChange={(value) =>
                                    value.trim() ? setFieldValue('phone', `+${value}`) : setFieldValue('phone', '')
                                }
                                onBlur={handleBlur}
                                value={values.phone}
                            />
                        </div>
                        <ValidationError
                            errors={errors}
                            touched={touched}
                            field="phone"
                            customMessage="Invalid phone number"
                        />
                    </div>
                    <div className="field">
                        <span className="required">{translate('link:email')}</span>
                        <input
                            name="email"
                            className={`input ${markAsError({ errors, touched, field: 'email' })}`}
                            type="text"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.email}
                        />
                        <ValidationError errors={errors} touched={touched} field="email" />
                    </div>
                    {schedulingLink?.settings.collectLinkedIn && (
                        <div className="field">
                            <span>{translate('link:linkedIn')}</span>
                            <input
                                name="linkedInUrl"
                                className={`input ${markAsError({ errors, touched, field: 'linkedInUrl' })}`}
                                type="text"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.linkedInUrl ?? ''}
                            />
                            <ValidationError errors={errors} touched={touched} field="linkedInUrl" />
                        </div>
                    )}
                </div>
                <div className="cardFooter">
                    <Button onClick={submitForm} className="continueButton">
                        {translate('link:continue')}
                    </Button>
                </div>
            </div>
        </Wrapper>
    );
};

export default SchedulingLinkData;
